import classNames from 'classnames';
import type { ComponentProps, CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import {
  formatScarcity,
  playableBlockchainRarities,
  scarcityNames,
} from '@sorare/core/src/lib/cards';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import type { Unit } from '@sorare/core/src/lib/style';

import { PickerOption } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { PickerOptionsGrid } from '#src/components/pickers/PickerOptionsGrid.tsx';

const StyledPickerOption = styled(PickerOption)`
  padding: var(--triple-unit);
`;

type Props = {
  rarities?: readonly Rarity[];
  selected: Rarity;
  onSelect: (rarity: Rarity) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

const SIZE_TO_ICON_SIZE: Record<
  NonNullable<ComponentProps<typeof ShieldButton>['size']>,
  Unit
> = {
  xxs: 1.5,
  xs: 2,
  sm: 3,
  md: 4,
  lg: 4,
  xl: 5,
};

export const RarityPicker = ({
  rarities = playableBlockchainRarities,
  selected,
  onSelect,
  size = 'md',
}: Props) => {
  if (rarities.length <= 1) {
    return null;
  }

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={scarcityNames[selected]}
          size={size}
          icon={
            <ScarcityIcon rarity={selected} size={SIZE_TO_ICON_SIZE[size]} />
          }
          orientation="down"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose Rarity" />
          </PickerHeader>
          <PickerOptionsGrid>
            {rarities.map(rarity => (
              <StyledPickerOption
                key={rarity}
                className={classNames({ selected: rarity === selected })}
                style={
                  { '--board-color': getRarityColor(rarity) } as CSSProperties
                }
                onClick={() => {
                  onSelect(rarity);
                  closeDropdown();
                }}
              >
                <Vertical gap={1.5}>
                  <ScarcityIcon rarity={rarity} size={4} />
                  <BrandXS>{formatScarcity(rarity)}</BrandXS>
                </Vertical>
              </StyledPickerOption>
            ))}
          </PickerOptionsGrid>
        </>
      )}
    </Dropdown>
  );
};
